/**
 * Apercu d'une piece jointe dans le coffre (« Quick Look » des documents de 1Password, apercu des fichiers de Proton
 * Pass) : une photo de passeport, un scan d'ordonnance ou un fichier de codes de secours se LIT sans etre telecharge.
 * Telecharger ecrit le fichier dechiffre EN CLAIR sur le disque (dossier Telechargements, sauvegardes, indexation) ;
 * l'apercu le garde en memoire, et il est oublie a la fermeture, au changement d'entree et au verrouillage.
 *
 * Le type se lit au CONTENU, jamais au nom : `photo.png` qui contient du HTML n'est pas une image, et un nom ne doit
 * jamais decider de ce qui est rendu dans l'origine du coffre.
 * - Image : signature PNG, JPEG, GIF ou WebP seulement. Le type MIME du rendu est celui de la signature. SVG est
 *   EXCLU : c'est un document scriptable -- ouverte dans un onglet depuis son URL `blob:`, une image SVG executerait
 *   ses scripts dans l'origine du coffre.
 * - Texte : UTF-8 valide (decodage strict), sans octet nul ni caractere de controle hors tabulation et fins de ligne,
 *   borne a `MAX_TEXT_PREVIEW_BYTES`. Rendu echappe dans un `<pre>`, jamais interprete (ni HTML, ni Markdown).
 * - Le reste (PDF, archives, bureautique...) n'a pas d'apercu : le message le dit et propose le telechargement.
 *
 * Pur, zero E/S : les octets dechiffres sont lus ici et ne sont gardes nulle part.
 */

/** Au-dela, un texte se telecharge : un apercu de plusieurs centaines de pages ne se lit pas, il fige la page. */
export const MAX_TEXT_PREVIEW_BYTES = 256 * 1024;

export type AttachmentPreviewImageType = 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp';

export type AttachmentPreview =
  | { readonly kind: 'image'; readonly mimeType: AttachmentPreviewImageType }
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'none'; readonly reason: string };

function startsWith(bytes: Uint8Array, signature: readonly number[], offset = 0): boolean {
  if (bytes.length < offset + signature.length) return false;
  return signature.every((byte, index) => bytes[offset + index] === byte);
}

const ascii = (text: string): number[] => [...text].map((char) => char.charCodeAt(0));

/** Type MIME d'une image reconnue a sa signature, ou `null` (SVG compris : jamais rendu). */
export function sniffPreviewImage(bytes: Uint8Array): AttachmentPreviewImageType | null {
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image/png';
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) return 'image/jpeg';
  if (startsWith(bytes, ascii('GIF87a')) || startsWith(bytes, ascii('GIF89a'))) return 'image/gif';
  if (startsWith(bytes, ascii('RIFF')) && startsWith(bytes, ascii('WEBP'), 8)) return 'image/webp';
  return null;
}

/** Caractere de controle qui trahit un fichier binaire : tout C0 sauf tabulation, fins de ligne et saut de page, plus DEL. */
function isBinaryControl(code: number): boolean {
  if (code === 0x09 || code === 0x0a || code === 0x0d || code === 0x0c) return false;
  return code < 0x20 || code === 0x7f;
}

/** Texte lisible tel quel, ou `null` : UTF-8 strict, aucun caractere de controle binaire. */
export function readPreviewText(bytes: Uint8Array): string | null {
  let text: string;
  try {
    text = new TextDecoder('utf-8', { fatal: true, ignoreBOM: false }).decode(bytes);
  } catch {
    return null;
  }
  for (let index = 0; index < text.length; index++) {
    if (isBinaryControl(text.charCodeAt(index))) return null;
  }
  return text;
}

/** Ce que l'apercu peut montrer de ces octets dechiffres. */
export function attachmentPreview(bytes: Uint8Array): AttachmentPreview {
  const mimeType = sniffPreviewImage(bytes);
  if (mimeType !== null) return { kind: 'image', mimeType };
  if (bytes.length === 0) return { kind: 'none', reason: 'Fichier vide : rien a montrer.' };
  const text = readPreviewText(bytes);
  if (text === null) {
    return {
      kind: 'none',
      reason: 'Pas d’apercu pour ce type de fichier (images PNG, JPEG, GIF, WebP et textes seulement). Telechargez-le pour l’ouvrir.',
    };
  }
  if (bytes.length > MAX_TEXT_PREVIEW_BYTES) {
    return { kind: 'none', reason: 'Texte trop long pour un apercu. Telechargez-le pour le lire en entier.' };
  }
  return { kind: 'text', text };
}
